import type {
  AxisDetailIndicator,
  MovementMeasureType,
  RadarAxis,
} from "@lemonadetraining/contracts";

/**
 * The published `AxisDetailIndicator` carries only `scoringType` (the scoring
 * shape post-LT-523). Display formatting needs the orthogonal **unit**, which
 * lives on the movement's `measureType` but is NOT on the shared type. We
 * attach it via this local extension (same pattern as
 * `MovementRegistryEntryWithMeta`) rather than churning the published type +
 * republishing. `computeAxisDetail` builds these from `MovementRegistryEntry`.
 */
export type AxisDetailIndicatorWithMeasure = AxisDetailIndicator & {
  measureType: MovementMeasureType;
};

/**
 * Visible level milestones for the in-bar markers. Mirrors the mobile
 * `ProgressBarWithLevels` defaults (Lv 50 / 60 / 70 / 80) so cross-platform
 * UX stays in lockstep.
 */
export const DEFAULT_VISIBLE_LEVELS: ReadonlyArray<number> = [50, 60, 70, 80];

export type LevelMilestone = {
  level: number;
  value: number;
};

/**
 * Linear normalized position (0..1) of a value within the [floor..ceiling]
 * range. Handles ascending ranges (weight/reps: floor < ceiling) and
 * descending ranges (time: floor > ceiling) — the formula is symmetric in
 * sign, so a current of 180s with floor=600, ceiling=100 yields the same
 * 84% the math intuitively predicts. Returns 0 only for the degenerate
 * `floor === ceiling` case.
 */
export function computeFillPercent(current: number, floor: number, ceiling: number): number {
  if (!Number.isFinite(current) || !Number.isFinite(floor) || !Number.isFinite(ceiling)) {
    return 0;
  }
  if (ceiling === floor) return 0;
  const raw = (current - floor) / (ceiling - floor);
  if (raw <= 0) return 0;
  if (raw >= 1) return 1;
  return raw;
}

/**
 * Pick the visible milestones for an indicator's `levelMilestones` map.
 *
 * Behaviour:
 *  - Filter to the canonical `DEFAULT_VISIBLE_LEVELS` set when available.
 *  - Drop levels that are missing from the indicator's map (no implicit zero).
 *  - Drop milestones outside `[floor, ceiling]` so they never render as a
 *    marker glued to either edge.
 *  - Sort ascending by level so screen-reader announcement order matches the
 *    visual left-to-right order.
 */
export function pickMilestones(
  levelMilestones: Record<number, number> | undefined,
  floor: number,
  ceiling: number
): LevelMilestone[] {
  if (!levelMilestones) return [];
  if (ceiling === floor) return [];
  // Time-scored bars use floor > ceiling (Lv 0 worst time → Lv 100 best
  // time); take the in-range bounds via Math.min/max so descending ranges
  // don't drop every marker.
  const lo = Math.min(floor, ceiling);
  const hi = Math.max(floor, ceiling);
  const out: LevelMilestone[] = [];
  for (const level of DEFAULT_VISIBLE_LEVELS) {
    const value = levelMilestones[level];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    if (value < lo || value > hi) continue;
    out.push({ level, value });
  }
  return out;
}

/**
 * Default contextual message shown when the API doesn't provide one.
 * Keep messages short and per-axis stable so the screen reader doesn't
 * announce wildly different copy on every render.
 */
export function defaultContextMessage(
  axis: RadarAxis,
  isWeakest: boolean,
  isStrongest: boolean
): string {
  if (isWeakest) return "This is your weakest axis — focus here for the biggest gains.";
  if (isStrongest) return "This is your strongest axis — keep pushing your edge.";
  return `Drill into ${axis} contributors and milestones below.`;
}

/**
 * Smallest visible-level milestone strictly above `currentLevel`. Returns
 * `null` when the athlete has cleared the highest milestone.
 */
export function nextVisibleLevel(currentLevel: number): number | null {
  for (const lv of DEFAULT_VISIBLE_LEVELS) {
    if (currentLevel < lv) return lv;
  }
  return null;
}

/**
 * Highest visible-level milestone the athlete has cleared. Returns `0` when
 * the athlete is below the lowest visible milestone.
 */
export function levelClearedFor(currentLevel: number): number {
  let cleared = 0;
  for (const lv of DEFAULT_VISIBLE_LEVELS) {
    if (currentLevel >= lv) cleared = lv;
  }
  return cleared;
}
